import {
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import clsx from "clsx";
import Tree from "components/Tree";
import { modalTypes } from "constants/types";
import { ReactComponent as ClosedIcon } from "icons/workspace/arrow_folder_closed_transparent.svg";
import { ReactComponent as OpenedIcon } from "icons/workspace/arrow_folder_open_unselected.svg";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import { populateErrorMessage } from "slices/app/errorSlice";
import { getFolders } from "slices/workspace/actions/folders";
import { setCurrentFolder } from "slices/workspace/foldersSlice";
import { getImageFromType } from "utils";
import handleKeyboardClick from "utils/handleKeyboardClick";

const SelectFolderModal = ({ title, onOk, ignore = [], reset = true }) => {
  const dispatch = useDispatch();
  const [folderName, setFolderName] = useState("");
  const folders = useSelector((state) => state.folders);
  const [result, setResult] = useState(undefined);
  const [data, setData] = useState([]);
  const [selected, setSelected] = useState(undefined);
  const [columns] = useState([{}]);

  let index = 1;
  const prepareResultData = (d) => {
    d = d.filter((c) => c.isFolder === "true" && ignore.indexOf(c.vtid) === -1);
    d.forEach((i) => {
      i.id = "" + index;
      index++;
      if (i.children) {
        i.children = prepareResultData(i.children);
      }
    });
    return d;
  };

  const processData = (response) => {
    setData({
      tree: {
        id: "0",
        children: response,
      },
    });
  };

  const handleSelection = (row) => {
    setSelected(row.vtid);
    setFolderName(row.name);
    processData(result);
  };

  const handleListSuccess = (r) => {
    prepareResultData(r);
    setResult(r);
    processData(r);
  };

  const handleListFailure = (error) => {
    dispatch(populateErrorMessage(error));
  };

  useEffect(() => {
    dispatch(getFolders(handleListSuccess, handleListFailure));
  }, []);

  const handleCancel = () => {
    dispatch(hideModal(modalTypes.SELECT_FOLDER));
  };

  const handleOk = () => {
    if (reset) {
      dispatch(
        setCurrentFolder({ folderId: Number(selected), folderName: folderName })
      );
    }
    dispatch(hideModal(modalTypes.SELECT_FOLDER));
    onOk({ ...folders, folderId: selected, folderName: folderName });
  };

  const createCell = (row, column, toggle, isOpen) => {
    const Icon = isOpen ? OpenedIcon : ClosedIcon;
    return (
      <div className="control_column">
        {!row.isLeaf ? (
          <span className="icon">
            <Icon
              alt="icon"
              data-testid="icon"
              title={isOpen ? "Close" : "Open"}
              onClick={toggle}
            />
          </span>
        ) : (
          <span style={{ padding: 0, width: 12, display: "inline-block" }} />
        )}
        <span style={{ padding: 0, width: 42, display: "inline-block" }}>
          <img
            className="row_type"
            src={getImageFromType(isOpen ? "folder-open" : "folder-closed")}
            alt="row type"
          />
        </span>
        <span
          className="title"
          data-clarity-mask="True"
          dangerouslySetInnerHTML={{ __html: row.text }}
        />
      </div>
    );
  };

  const renderRow = ({ data: row, isOpen, style, toggle }) => {
    const rowIndex = parseInt(row.id);
    return (
      <div style={style}>
        {row.isRoot === false ? (
          <>
            <div
              role="button"
              tabIndex={0}
              className={clsx(
                "tree_body_row",
                isOpen && "expanded",
                rowIndex === selected && "selected",
                selected === row.vtid && "selected"
              )}
              onClick={() => {
                setSelected(rowIndex);
                handleSelection(row);
                return false;
              }}
              onKeyDown={handleKeyboardClick(() => {
                setSelected(rowIndex);
                handleSelection(row);
                return false;
              })}
            >
              {columns.map((column, i) => {
                const columnStyle = {
                  width: columns[i].width,
                  minWidth: columns[i].width,
                  maxWidth: columns[i].width,
                  flex: 1,
                };
                if (i === 0) {
                  columnStyle.paddingLeft = `${20 * row.nestingLevel}px`;
                }
                return (
                  <div className="tree_cell tree_body_cell" style={columnStyle}>
                    {createCell(row, i, toggle, isOpen)}
                  </div>
                );
              })}
            </div>
          </>
        ) : null}
      </div>
    );
  };

  return (
    <Modal
      data-testid="choose-folder-modal"
      className="fade-in choose_folder_modal"
      show
      onDismiss={handleCancel}
      backdrop
      centered
    >
      <Modal.Header>
        <b>{title}</b>
      </Modal.Header>
      <Modal.Body>
        <Tree
          data={data}
          renderRow={renderRow}
          columns={columns}
          displayHeader={false}
        />
      </Modal.Body>
      <Modal.Footer>
        <NewButton
          data-testid="cancel-button"
          variant={NewButtonVariant.l2}
          onClick={handleCancel}
        >
          Cancel
        </NewButton>

        <NewButton
          data-testid="ok-button"
          variant={NewButtonVariant.l1}
          onClick={handleOk}
          disabled={selected === undefined}
        >
          OK
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

export default SelectFolderModal;

SelectFolderModal.propTypes = {
  title: PropTypes.string.isRequired,
  onOk: PropTypes.func,
  reset: PropTypes.bool,
  ignore: PropTypes.arrayOf(PropTypes.number),
};
